import React, { useState, useContext, useEffect } from "react";
import clsx from "clsx";
import { Link as RouterLink, useLocation } from "react-router-dom";
import {
  makeStyles,
  Drawer,
  AppBar,
  Toolbar,
  List,
  Typography,
  Divider,
  MenuItem,
  IconButton,
  Menu,
  Button,
  Badge,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Checkbox,
  ListItemText,
  Tooltip,
} from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import ChevronLeftIcon from "@material-ui/icons/ChevronLeft";
import AccountCircle from "@material-ui/icons/AccountCircle";
import ChatIcon from "@material-ui/icons/Chat";
import LaunchIcon from "@material-ui/icons/Launch";
import VisibilityIcon from "@material-ui/icons/Visibility";
import StarIcon from "@material-ui/icons/Star";
import StarBorderIcon from "@material-ui/icons/StarBorder";

import MainListItems, { hasMenuPermission } from "./MainListItems";
import NotificationsPopOver from "../components/NotificationsPopOver";
import UserModal from "../components/UserModal";
import DashboardWebChatScript from "../components/DashboardWebChatScript";
import { AuthContext } from "../context/Auth/AuthContext";
import BackdropLoading from "../components/BackdropLoading";
import { i18n } from "../translate/i18n";
import api from "../services/api";
import toastError from "../errors/toastError";
import { toast } from "react-toastify";
import {
  applyBranding,
  applyResellerBranding,
  getCachedBranding,
  resolveBrandingUrl,
} from "../utils/branding";
import {
  openSidepanelApp,
  syncSidepanelAppsWithExtension,
  updateSidepanelContext,
} from "../utils/sidepanelApps";
import { activeTicketContextEvent } from "../utils/ticketEvents";
import {
  getStoredPrivacyCensorSettings,
  hasActivePrivacyCensor,
  privacyCensorChangedEvent,
  setStoredPrivacyCensorSettings,
} from "../utils/privacyCensor";

const drawerWidth = 290;
const drawerClosedWidth = 80;
const getAssignmentMode = mode =>
  mode === "normal" ? "normal" : "multiagent";
const getShowAllNormalMenu = value => value === true || value === "true";

const normalizeExpirationBadgeLink = value => {
  const trimmedValue = String(value || "").trim();
  if (!trimmedValue) return "";

  if (
    /^https?:\/\//i.test(trimmedValue) ||
    /^mailto:/i.test(trimmedValue) ||
    /^tel:/i.test(trimmedValue) ||
    trimmedValue.startsWith("/")
  ) {
    return trimmedValue;
  }

  return `https://${trimmedValue}`;
};

const normalizeExternalUrl = normalizeExpirationBadgeLink;

const normalizeExpirationBadgeLinks = links => {
  let parsedLinks = [];

  if (Array.isArray(links)) {
    parsedLinks = links;
  } else if (typeof links === "string" && links.trim()) {
    try {
      const parsed = JSON.parse(links);
      parsedLinks = Array.isArray(parsed) ? parsed : [];
    } catch {
      parsedLinks = [];
    }
  }

  return parsedLinks
    .map((link, index) => ({
      label: String(link?.label || `Link ${index + 1}`).trim(),
      url: normalizeExpirationBadgeLink(link?.url),
    }))
    .filter(link => link.label && link.url);
};

const normalizeSidepanelApps = apps =>
  (Array.isArray(apps) ? apps : [])
    .filter(app => app?.url)
    .map(app => ({
      ...app,
      id: String(app.id),
      name: String(app.name || "App sin nombre").trim(),
      url: String(app.url || "").trim(),
      scope: app.scope === "global" ? "global" : "company",
      isDefault: Boolean(app.isDefault),
      zoomLevel: Number(app.zoomLevel || 100),
    }));

const applySingleDefaultSidepanelApp = (apps, selectedApp) =>
  apps.map(app => ({
    ...app,
    isDefault:
      String(app.id) === String(selectedApp.id) &&
      app.scope === selectedApp.scope,
  }));

const useStyles = makeStyles((theme) => ({
  root: {
    display: "flex",
    height: "100vh",
    [theme.breakpoints.down("sm")]: {
      height: "100vh",
    },
  },
  toolbar: {
    paddingRight: 24, // keep right padding when drawer closed
  },
  toolbarIcon: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    padding: "0 8px",
    minHeight: "48px",
  },
  drawerLogo: {
    display: "block",
    width: "100%",
    height: 38,
    objectFit: "contain",
  },
  resellerDrawerBrand: {
    alignItems: "center",
    display: "flex",
    flex: 1,
    justifyContent: "center",
    minWidth: 0,
    padding: theme.spacing(1, 0.5),
    textAlign: "left",
  },
  resellerDrawerLogo: {
    borderRadius: 8,
    display: "block",
    height: 32,
    objectFit: "cover",
    width: 32,
  },
  resellerDrawerTitle: {
    fontSize: 12,
    fontWeight: 700,
    lineHeight: 1.2,
    marginLeft: theme.spacing(1),
    maxWidth: "100%",
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  appBar: {
    zIndex: theme.zIndex.drawer + 1,
    transition: "none",
    backgroundColor: theme.palette.background.default,
    boxShadow:
      "0px 2px 4px -1px rgb(0 0 0 / 5%), 0px 4px 5px 0px rgba(0, 0, 0, 0.14), 0px 1px 10px 0px rgb(0 0 0 / 5%)",
  },
  appBarShift: {
    marginLeft: drawerWidth,
    width: `calc(100% - ${drawerWidth}px)`,
    transition: "none",
  },
  menuButton: {
    marginLeft: -10,
    marginRight: 36,
    color: theme.palette.text.primary,
  },
  menuButtonHidden: {
    display: "none",
  },
  title: {
    flexGrow: 1,
    color: theme.palette.text.primary,
  },
  headerMenuItems: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1),
    marginRight: theme.spacing(2),
    overflowX: "auto",
    maxWidth: "34vw",
    ...theme.scrollbarStyles,
    [theme.breakpoints.down("sm")]: {
      display: "none",
    },
  },
  headerMenuButton: {
    minHeight: 32,
    whiteSpace: "nowrap",
  },
  headerMenuIcon: {
    marginRight: theme.spacing(0.75),
  },
  headerMenuLaunchIcon: {
    fontSize: 16,
    marginLeft: theme.spacing(0.5),
  },
  sidepanelAppMenuItem: {
    minWidth: 220,
    paddingRight: theme.spacing(1),
  },
  sidepanelAppMenuText: {
    alignItems: "center",
    display: "flex",
    flex: 1,
    minWidth: 0,
  },
  sidepanelAppMenuName: {
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  sidepanelDefaultButton: {
    marginLeft: theme.spacing(1),
    padding: 4,
  },
  sidepanelDefaultIcon: {
    fontSize: 18,
  },
  expirationChip: {
    marginRight: theme.spacing(2),
    fontWeight: 600,
  },
  supportChatBadge: {
    marginRight: theme.spacing(2),
  },
  supportChatButton: {
    whiteSpace: "nowrap",
    [theme.breakpoints.down("xs")]: {
      minWidth: 40,
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
      "& .MuiButton-startIcon": {
        marginRight: 0,
        marginLeft: 0,
      },
      "& .supportChatButtonText": {
        display: "none",
      },
    },
  },
  assignmentModeToggle: {
    display: "inline-grid",
    gridTemplateColumns: "1fr 1fr",
    alignItems: "center",
    width: 250,
    padding: 3,
    marginRight: theme.spacing(2),
    borderRadius: 999,
    border: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.paper,
    boxSizing: "border-box",
    [theme.breakpoints.down("xs")]: {
      display: "none",
    },
  },
  assignmentModeOption: {
    height: 28,
    minWidth: 0,
    padding: theme.spacing(0, 1),
    border: 0,
    borderRadius: 999,
    cursor: "pointer",
    fontSize: 12,
    fontWeight: 700,
    letterSpacing: 0,
    color: theme.palette.text.disabled,
    backgroundColor: "transparent",
    transition: "background-color 160ms ease, color 160ms ease",
    whiteSpace: "nowrap",
    "&:disabled": {
      cursor: "default",
    },
  },
  assignmentModeOptionActive: {
    color: theme.palette.primary.contrastText,
    backgroundColor: theme.palette.primary.main,
    boxShadow: "0 1px 3px rgba(0, 0, 0, 0.18)",
  },
  drawerPaper: {
    position: "relative",
    whiteSpace: "nowrap",
    width: drawerWidth,
    transition: "none",
    backgroundColor: theme.palette.background.paper,
  },
  drawerPaperClose: {
    overflowX: "hidden",
    transition: "none",
    width: drawerClosedWidth,
    [theme.breakpoints.up("sm")]: {
      width: drawerClosedWidth,
    },
  },
  appBarSpacer: {
    minHeight: "48px",
  },
  content: {
    flex: 1,
    overflow: "auto",
  },
  container: {
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
  },
  paper: {
    padding: theme.spacing(2),
    display: "flex",
    overflow: "auto",
    flexDirection: "column",
  },
  iconButton: {
    color: theme.palette.text.primary,
  },
  privacyButtonActive: {
    color: "#148040",
  },
  impersonationBar: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(2),
    padding: theme.spacing(1, 2),
    color: theme.palette.common.white,
    backgroundColor: theme.palette.warning.dark,
  },
  impersonationText: {
    flex: 1,
    fontWeight: 600,
  },
}));

const formatExpirationBadge = expiresAt => {
  if (!expiresAt) return null;

  const expirationDate = new Date(expiresAt);
  if (Number.isNaN(expirationDate.getTime())) return null;

  const today = new Date();
  const startOfToday = new Date(
    today.getFullYear(),
    today.getMonth(),
    today.getDate()
  );
  const startOfExpiration = new Date(
    expirationDate.getFullYear(),
    expirationDate.getMonth(),
    expirationDate.getDate()
  );
  const remainingDays = Math.ceil(
    (startOfExpiration.getTime() - startOfToday.getTime()) /
      (1000 * 60 * 60 * 24)
  );
  const formattedDate = expirationDate.toLocaleDateString("es-CR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });

  if (remainingDays < 0) {
    return {
      label: `Expirado ${formattedDate}`,
      color: "secondary",
    };
  }

  return {
    label: `Expira ${formattedDate} - quedan ${remainingDays} dias`,
    color: remainingDays <= 7 ? "secondary" : "primary",
  };
};

const LoggedInLayout = ({ children }) => {
  const classes = useStyles();
  const location = useLocation();
  const [userModalOpen, setUserModalOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const {
    handleLogout,
    loading,
    impersonation,
    handleStopImpersonation,
  } = useContext(AuthContext);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [drawerVariant, setDrawerVariant] = useState("permanent");
  const { user } = useContext(AuthContext);
  const assignmentModeStorageKey = `ticketAssignmentMode:${
    user?.companyId || user?.company?.id || "default"
  }`;
  const [assignmentMode, setAssignmentMode] = useState(() => {
    const storedMode = localStorage.getItem(assignmentModeStorageKey);
    return getAssignmentMode(storedMode);
  });
  const showAllNormalMenuStorageKey = `normalModeShowAllMenu:${
    user?.companyId || user?.company?.id || "default"
  }`;
  const [showAllNormalMenu, setShowAllNormalMenu] = useState(() =>
    getShowAllNormalMenu(localStorage.getItem(showAllNormalMenuStorageKey))
  );
  const [pendingAssignmentMode, setPendingAssignmentMode] = useState(null);
  const [savingAssignmentMode, setSavingAssignmentMode] = useState(false);
  const [branding, setBranding] = useState(null);
  const [showHeaderWebChat, setShowHeaderWebChat] = useState(false);
  const [supportChatUnread, setSupportChatUnread] = useState(0);
  const [accountMenuItems, setAccountMenuItems] = useState([]);
  const [accountMenuCategories, setAccountMenuCategories] = useState([]);
  const [headerCategoryMenu, setHeaderCategoryMenu] = useState(null);
  const [sidepanelAppsAnchor, setSidepanelAppsAnchor] = useState(null);
  const [sidepanelGlobalApps, setSidepanelGlobalApps] = useState([]);
  const [sidepanelCompanyApps, setSidepanelCompanyApps] = useState([]);
  const [loadingSidepanelApps, setLoadingSidepanelApps] = useState(false);
  const [activeTicketContext, setActiveTicketContext] = useState(null);
  const [privacyMenuAnchor, setPrivacyMenuAnchor] = useState(null);
  const [privacyCensorSettings, setPrivacyCensorSettings] = useState(() =>
    getStoredPrivacyCensorSettings(user?.id)
  );
  const [dashboardWebChatSettingsVersion, setDashboardWebChatSettingsVersion] =
    useState(0);
  const [expirationBadgeLinks, setExpirationBadgeLinks] = useState([]);
  const [expirationLinksAnchor, setExpirationLinksAnchor] = useState(null);
  const [sidepanelExtensionInstallUrl, setSidepanelExtensionInstallUrl] =
    useState("");
  const [sidepanelAppsHeaderButtonEnabled, setSidepanelAppsHeaderButtonEnabled] =
    useState(true);
  const [sidepanelExtensionModalOpen, setSidepanelExtensionModalOpen] =
    useState(false);
  const isResellerCustomer = Boolean(user?.company?.resellerCompanyId);
  const expirationBadge = isResellerCustomer
    ? null
    : formatExpirationBadge(user?.company?.expiresAt);
  const hasExpirationBadgeLinks = expirationBadgeLinks.length > 0;
  const forceNormalMode = user?.company?.forceNormalMode === true;
  const canManageAssignmentMode = ["admin", "superadmin", "owner"].includes(
    user?.profile
  );

  useEffect(() => {
    if (document.body.offsetWidth > 600) {
      setDrawerOpen(true);
    }
  }, []);

  useEffect(() => {
    setPrivacyCensorSettings(getStoredPrivacyCensorSettings(user?.id));
  }, [user?.id]);

  useEffect(() => {
    const handlePrivacyCensorChanged = event => {
      setPrivacyCensorSettings(
        event.detail || getStoredPrivacyCensorSettings(user?.id)
      );
    };

    window.addEventListener(
      privacyCensorChangedEvent,
      handlePrivacyCensorChanged
    );

    return () => {
      window.removeEventListener(
        privacyCensorChangedEvent,
        handlePrivacyCensorChanged
      );
    };
  }, [user?.id]);

  useEffect(() => {
    if (document.body.offsetWidth < 600) {
      setDrawerVariant("temporary");
    } else {
      setDrawerVariant("permanent");
    }
  }, [drawerOpen]);

  useEffect(() => {
    const fetchAssignmentMode = async () => {
      try {
        const { data } = await api.get("/settings/ticket-assignment-mode");
        const nextMode = getAssignmentMode(data.mode);
        localStorage.setItem(assignmentModeStorageKey, nextMode);
        setAssignmentMode(nextMode);
      } catch (err) {
        toastError(err);
      }
    };

    fetchAssignmentMode();
  }, [assignmentModeStorageKey]);

  useEffect(() => {
    if (!forceNormalMode) return;

    localStorage.setItem(assignmentModeStorageKey, "normal");
    setAssignmentMode("normal");
    window.dispatchEvent(
      new CustomEvent("ticketAssignmentModeChanged", {
        detail: { mode: "normal" },
      })
    );
  }, [assignmentModeStorageKey, forceNormalMode]);

  useEffect(() => {
    const fetchShowAllNormalMenu = async () => {
      try {
        const { data } = await api.get("/settings/normal-mode-show-all-menu");
        const nextValue = Boolean(data.showAll);
        localStorage.setItem(showAllNormalMenuStorageKey, String(nextValue));
        setShowAllNormalMenu(nextValue);
      } catch (err) {
        toastError(err);
      }
    };

    const handleShowAllNormalMenuChanged = event => {
      const nextValue = Boolean(event.detail?.showAll);
      localStorage.setItem(showAllNormalMenuStorageKey, String(nextValue));
      setShowAllNormalMenu(nextValue);
    };

    fetchShowAllNormalMenu();
    window.addEventListener(
      "normalModeShowAllMenuChanged",
      handleShowAllNormalMenuChanged
    );

    return () => {
      window.removeEventListener(
        "normalModeShowAllMenuChanged",
        handleShowAllNormalMenuChanged
      );
    };
  }, [showAllNormalMenuStorageKey]);

  useEffect(() => {
    let mounted = true;

    const fetchAccountMenu = async () => {
      if (!user?.id) return;

      try {
        const { data } = await api.get("/account-menu");
        if (mounted) {
          setAccountMenuItems(data.items || []);
          setAccountMenuCategories(data.categories || []);
        }
      } catch {
        if (mounted) {
          setAccountMenuItems([]);
          setAccountMenuCategories([]);
        }
      }
    };

    const handleAccountMenuUpdate = event => {
      if (mounted && Array.isArray(event.detail?.items)) {
        setAccountMenuItems(event.detail.items);
        setAccountMenuCategories(event.detail.categories || []);
      }
    };

    fetchAccountMenu();
    window.addEventListener("account-menu-updated", handleAccountMenuUpdate);

    return () => {
      mounted = false;
      window.removeEventListener("account-menu-updated", handleAccountMenuUpdate);
    };
  }, [user?.id]);

  useEffect(() => {
    let mounted = true;

    const fetchSidepanelApps = async ({ showLoading = false } = {}) => {
      if (!user?.id || !hasMenuPermission(user, "sidepanelApps")) {
        setSidepanelGlobalApps([]);
        setSidepanelCompanyApps([]);
        return;
      }

      if (showLoading) setLoadingSidepanelApps(true);

      try {
        const { data } = await api.get("/apps");
        const globalApps = normalizeSidepanelApps(data.globalApps || []);
        const companyApps = normalizeSidepanelApps(data.companyApps || []);

        if (mounted) {
          setSidepanelGlobalApps(globalApps);
          setSidepanelCompanyApps(companyApps);
        }

        syncSidepanelAppsWithExtension([...globalApps, ...companyApps]).catch(
          () => undefined
        );
      } catch {
        if (mounted) {
          setSidepanelGlobalApps([]);
          setSidepanelCompanyApps([]);
        }
      } finally {
        if (mounted && showLoading) setLoadingSidepanelApps(false);
      }
    };

    const handleSidepanelAppsUpdated = () => fetchSidepanelApps();

    fetchSidepanelApps({ showLoading: true });
    window.addEventListener("sidepanel-apps-updated", handleSidepanelAppsUpdated);

    return () => {
      mounted = false;
      window.removeEventListener(
        "sidepanel-apps-updated",
        handleSidepanelAppsUpdated
      );
    };
  }, [user?.id, user?.profile, user?.companyId]);

  useEffect(() => {
    const handleActiveTicketContext = event => {
      setActiveTicketContext(event.detail?.context || null);
    };

    window.addEventListener(
      activeTicketContextEvent,
      handleActiveTicketContext
    );

    return () => {
      window.removeEventListener(
        activeTicketContextEvent,
        handleActiveTicketContext
      );
    };
  }, []);

  useEffect(() => {
    if (!activeTicketContext) return;

    updateSidepanelContext(activeTicketContext).catch(() => undefined);
  }, [activeTicketContext]);

  useEffect(() => {
    const resellerBranding = user?.company?.resellerBranding;
    const hasResellerCompany = Boolean(user?.company?.resellerCompanyId);

    if (resellerBranding) {
      setBranding(applyResellerBranding(resellerBranding));
      return undefined;
    }

    if (hasResellerCompany) {
      setBranding(currentBranding => currentBranding || {
        logoUrl: "",
        faviconUrl: "data:,",
        siteTitle: "",
      });
      return undefined;
    }

    api
      .get("/settings/public/branding")
      .then(({ data }) => setBranding(applyBranding(data)))
      .catch(() => undefined);

    const handleBrandingChanged = event => {
      setBranding(event.detail || getCachedBranding());
    };

    window.addEventListener("brandingChanged", handleBrandingChanged);

    return () => {
      window.removeEventListener("brandingChanged", handleBrandingChanged);
    };
  }, [user?.company?.resellerBranding, user?.company?.resellerCompanyId]);

  useEffect(() => {
    let mounted = true;

    const fetchSidepanelExtensionInstallUrl = async () => {
      if (!user?.id) {
        setSidepanelExtensionInstallUrl("");
        return;
      }

      try {
        const { data } = await api.get(
          "/settings/sidepanel-extension-install-url"
        );
        if (mounted) {
          setSidepanelExtensionInstallUrl(normalizeExternalUrl(data?.value));
          setSidepanelAppsHeaderButtonEnabled(data?.headerButtonEnabled !== false);
        }
      } catch {
        if (mounted) {
          setSidepanelExtensionInstallUrl("");
          setSidepanelAppsHeaderButtonEnabled(true);
        }
      }
    };

    const handleSidepanelExtensionInstallUrlChanged = event => {
      setSidepanelExtensionInstallUrl(
        normalizeExternalUrl(event.detail?.value)
      );
      if (event.detail?.headerButtonEnabled !== undefined) {
        const enabled = event.detail.headerButtonEnabled !== false;
        setSidepanelAppsHeaderButtonEnabled(enabled);
        if (!enabled) setSidepanelAppsAnchor(null);
      }
    };

    fetchSidepanelExtensionInstallUrl();
    window.addEventListener(
      "sidepanelExtensionInstallUrlChanged",
      handleSidepanelExtensionInstallUrlChanged
    );

    return () => {
      mounted = false;
      window.removeEventListener(
        "sidepanelExtensionInstallUrlChanged",
        handleSidepanelExtensionInstallUrlChanged
      );
    };
  }, [user?.id]);

  useEffect(() => {
    let mounted = true;

    const fetchExpirationBadgeLink = async () => {
      if (!user?.id) {
        setExpirationBadgeLinks([]);
        return;
      }

      try {
        const { data } = await api.get("/settings/crm-expiration-badge-link");
        if (mounted) {
          setExpirationBadgeLinks(
            normalizeExpirationBadgeLinks(data?.links)
          );
        }
      } catch {
        if (mounted) setExpirationBadgeLinks([]);
      }
    };

    const handleExpirationBadgeLinkChanged = event => {
      setExpirationBadgeLinks(
        normalizeExpirationBadgeLinks(event.detail?.links)
      );
    };

    fetchExpirationBadgeLink();
    window.addEventListener(
      "crmExpirationBadgeLinkChanged",
      handleExpirationBadgeLinkChanged
    );

    return () => {
      mounted = false;
      window.removeEventListener(
        "crmExpirationBadgeLinkChanged",
        handleExpirationBadgeLinkChanged
      );
    };
  }, [user?.id]);

  const handleOpenExpirationLinks = event => {
    if (!hasExpirationBadgeLinks) return;
    setExpirationLinksAnchor(event.currentTarget);
  };

  const handleCloseExpirationLinks = () => {
    setExpirationLinksAnchor(null);
  };

  useEffect(() => {
    let mounted = true;

    const fetchDashboardWebChatSettings = async () => {
      if (!user?.id) {
        setShowHeaderWebChat(false);
        return;
      }

      try {
        const { data } = await api.get("/settings/dashboard/webchat-script");
        const snippet = (data?.value || "").trim();
        const launcherMode = data?.launcherMode === "header" ? "header" : "bubble";

        if (mounted) {
          setShowHeaderWebChat(
            Boolean(snippet && launcherMode === "header")
          );
        }
      } catch {
        if (mounted) setShowHeaderWebChat(false);
      }
    };

    const handleDashboardWebChatSettingsChanged = () => {
      setDashboardWebChatSettingsVersion(version => version + 1);
    };

    fetchDashboardWebChatSettings();
    window.addEventListener(
      "dashboardWebChatSettingsChanged",
      handleDashboardWebChatSettingsChanged
    );
    return () => {
      mounted = false;
      window.removeEventListener(
        "dashboardWebChatSettingsChanged",
        handleDashboardWebChatSettingsChanged
      );
    };
  }, [user?.id, location.pathname, dashboardWebChatSettingsVersion]);

  useEffect(() => {
    const handleSupportChatUnread = event => {
      setSupportChatUnread(Number(event?.detail?.count || 0));
    };

    window.addEventListener(
      "kodelrDashboardWebChatUnread",
      handleSupportChatUnread
    );

    return () => {
      window.removeEventListener(
        "kodelrDashboardWebChatUnread",
        handleSupportChatUnread
      );
    };
  }, []);

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
    setMenuOpen(true);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setMenuOpen(false);
  };

  const handleOpenUserModal = () => {
    setUserModalOpen(true);
    handleCloseMenu();
  };

  const handleClickLogout = () => {
    handleCloseMenu();
    handleLogout();
  };

  const drawerClose = () => {
    if (document.body.offsetWidth < 600) {
      setDrawerOpen(false);
    }
  };

  const handleOpenSupportChat = () => {
    window.dispatchEvent(new CustomEvent("kodelrDashboardWebChatToggle"));
  };

  const handleOpenSidepanelApp = async app => {
    setSidepanelAppsAnchor(null);

    try {
      const response = await openSidepanelApp(app, activeTicketContext || {});

      if (!response?.success) {
        throw new Error(response?.error || "No se pudo abrir el sidepanel.");
      }
    } catch (err) {
      setSidepanelExtensionModalOpen(true);
    }
  };

  const handleSetDefaultSidepanelApp = async (event, app) => {
    event.preventDefault();
    event.stopPropagation();

    if (app.isDefault) return;

    const previousGlobalApps = sidepanelGlobalApps;
    const previousCompanyApps = sidepanelCompanyApps;
    const nextGlobalApps = applySingleDefaultSidepanelApp(
      previousGlobalApps,
      app
    );
    const nextCompanyApps = applySingleDefaultSidepanelApp(
      previousCompanyApps,
      app
    );

    setSidepanelGlobalApps(nextGlobalApps);
    setSidepanelCompanyApps(nextCompanyApps);

    try {
      await api.put(`/apps/${app.id}`, { isDefault: true });
      syncSidepanelAppsWithExtension([...nextGlobalApps, ...nextCompanyApps]).catch(
        () => undefined
      );
    } catch (err) {
      setSidepanelGlobalApps(previousGlobalApps);
      setSidepanelCompanyApps(previousCompanyApps);
      toastError(err);
    }
  };

  const handleTogglePrivacyCensor = key => {
    setPrivacyCensorSettings(prevSettings =>
      setStoredPrivacyCensorSettings(user?.id, {
        ...prevSettings,
        [key]: !prevSettings[key],
      })
    );
  };

  const handleRequestAssignmentMode = mode => {
    if (
      forceNormalMode ||
      !canManageAssignmentMode ||
      mode === assignmentMode ||
      savingAssignmentMode
    ) {
      return;
    }

    setPendingAssignmentMode(mode);
  };

  const handleCloseAssignmentModeDialog = () => {
    if (!savingAssignmentMode) {
      setPendingAssignmentMode(null);
    }
  };

  const handleConfirmAssignmentMode = async () => {
    if (!pendingAssignmentMode) return;

    setSavingAssignmentMode(true);
    try {
      const { data } = await api.put("/settings/ticket-assignment-mode", {
        mode: pendingAssignmentMode,
      });
      const nextMode = getAssignmentMode(data.mode);
      localStorage.setItem(assignmentModeStorageKey, nextMode);
      setAssignmentMode(nextMode);
      window.dispatchEvent(
        new CustomEvent("ticketAssignmentModeChanged", {
          detail: { mode: nextMode },
        })
      );
      setPendingAssignmentMode(null);
    } catch (err) {
      toastError(err);
    } finally {
      setSavingAssignmentMode(false);
    }
  };

  const canRenderHeaderMenuItem = item => {
    if (!item.isActive) return false;
    if (item.type !== "builtin") return true;
    if (
      assignmentMode === "normal" &&
      !showAllNormalMenu &&
      ["internalChat", "users", "queues"].includes(item.builtinKey)
    ) {
      return false;
    }
    return hasMenuPermission(user, item.builtinKey);
  };

  const getHeaderMenuTarget = item => {
    if (item.type === "external") {
      return {
        component: "a",
        href: item.url || "#",
        target: "_blank",
        rel: "noopener noreferrer"
      };
    }

    return {
      component: RouterLink,
      to: item.type === "embed" ? `/menu-embed/${item.id}` : item.path || "/"
    };
  };

  const headerMenuItems = accountMenuItems
    .filter(item => item.section === "header" && !item.categoryId)
    .filter(canRenderHeaderMenuItem)
    .sort((a, b) => Number(a.position || 0) - Number(b.position || 0));

  const headerCategories = accountMenuCategories
    .filter(category => category.section === "header" && category.isActive !== false)
    .sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
    .map(category => ({
      ...category,
      items: accountMenuItems
        .filter(item => Number(item.categoryId) === Number(category.id))
        .filter(canRenderHeaderMenuItem)
        .sort((a, b) => Number(a.position || 0) - Number(b.position || 0))
    }))
    .filter(category => category.items.length > 0);

  const canShowSidepanelAppsMenu =
    sidepanelAppsHeaderButtonEnabled && hasMenuPermission(user, "sidepanelApps");
  const hasSidepanelApps =
    sidepanelGlobalApps.length > 0 || sidepanelCompanyApps.length > 0;
  const renderSidepanelAppMenuItem = (app, keyPrefix) => (
    <MenuItem
      key={`${keyPrefix}-sidepanel-app-${app.id}`}
      className={classes.sidepanelAppMenuItem}
      onClick={() => handleOpenSidepanelApp(app)}
    >
      <span className={classes.sidepanelAppMenuText}>
        <span className={classes.headerMenuIcon}>{"\uD83D\uDCF1"}</span>
        <span className={classes.sidepanelAppMenuName}>{app.name}</span>
      </span>
      <Tooltip
        title={
          app.isDefault
            ? "App predeterminada"
            : "Marcar como predeterminada"
        }
      >
        <IconButton
          size="small"
          className={classes.sidepanelDefaultButton}
          color={app.isDefault ? "primary" : "default"}
          onClick={event => handleSetDefaultSidepanelApp(event, app)}
        >
          {app.isDefault ? (
            <StarIcon className={classes.sidepanelDefaultIcon} />
          ) : (
            <StarBorderIcon className={classes.sidepanelDefaultIcon} />
          )}
        </IconButton>
      </Tooltip>
    </MenuItem>
  );

  if (loading || !branding) {
    return <BackdropLoading />;
  }

  const hasResellerBranding = Boolean(user?.company?.resellerBranding);
  const hideResellerHeaderControls = hasResellerBranding;
  const drawerLogoUrl = resolveBrandingUrl(branding.logoUrl) || "/logo.png";
  const drawerBrandTitle = branding.siteTitle || "CRM Multiagente";

  return (
    <div className={classes.root}>
      <DashboardWebChatScript />
      <Drawer
        variant={drawerVariant}
        className={drawerOpen ? classes.drawerPaper : classes.drawerPaperClose}
        classes={{
          paper: clsx(
            classes.drawerPaper,
            !drawerOpen && classes.drawerPaperClose
          ),
        }}
        open={drawerOpen}
      >
        <div className={classes.toolbarIcon}>
          {drawerOpen &&
            (hasResellerBranding ? (
              <div className={classes.resellerDrawerBrand}>
                <img
                  src={drawerLogoUrl}
                  alt={drawerBrandTitle}
                  className={classes.resellerDrawerLogo}
                />
                <Typography className={classes.resellerDrawerTitle}>
                  {drawerBrandTitle}
                </Typography>
              </div>
            ) : (
              <img
                src={drawerLogoUrl}
                alt="CRM Multiagente"
                className={classes.drawerLogo}
              />
            ))}
          <IconButton onClick={() => setDrawerOpen(!drawerOpen)}>
            <ChevronLeftIcon />
          </IconButton>
        </div>
        <Divider />
        <List>
          <MainListItems
            drawerClose={drawerClose}
            drawerOpen={drawerOpen}
            assignmentMode={assignmentMode}
            showAllNormalMenu={showAllNormalMenu}
          />
        </List>
        <Divider />
      </Drawer>
      <UserModal
        open={userModalOpen}
        onClose={() => setUserModalOpen(false)}
        userId={user?.id}
      />
      <AppBar
        position="absolute"
        className={clsx(classes.appBar, drawerOpen && classes.appBarShift)}
      >
        <Toolbar variant="dense" className={classes.toolbar}>
          <IconButton
            edge="start"
            aria-label="open drawer"
            onClick={() => setDrawerOpen(!drawerOpen)}
            className={clsx(
              classes.menuButton,
              drawerOpen && classes.menuButtonHidden
            )}
          >
            <MenuIcon />
          </IconButton>
          <Typography
            component="h1"
            variant="h6"
            noWrap
            className={classes.title}
          >
            Hola 👋 {user?.name || ""}
          </Typography>

          {(headerMenuItems.length > 0 || headerCategories.length > 0) && (
            <div className={classes.headerMenuItems}>
              {headerCategories.map(category => (
                <React.Fragment key={`header-category-${category.id}`}>
                  <Button
                    size="small"
                    color="primary"
                    className={classes.headerMenuButton}
                    onClick={event =>
                      setHeaderCategoryMenu({
                        id: category.id,
                        anchorEl: event.currentTarget
                      })
                    }
                  >
                    <span
                      className={classes.headerMenuIcon}
                      style={{
                        backgroundColor: category.color || "#2F80ED",
                        borderRadius: 999,
                        height: 10,
                        width: 10
                      }}
                    />
                    {category.name}
                  </Button>
                  <Menu
                    anchorEl={headerCategoryMenu?.anchorEl}
                    open={Number(headerCategoryMenu?.id) === Number(category.id)}
                    onClose={() => setHeaderCategoryMenu(null)}
                    getContentAnchorEl={null}
                    anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                    transformOrigin={{ vertical: "top", horizontal: "left" }}
                  >
                    {category.items.map(item => (
                      <MenuItem
                        key={`${item.type}-${item.id || item.builtinKey}`}
                        {...getHeaderMenuTarget(item)}
                        onClick={() => setHeaderCategoryMenu(null)}
                      >
                        <span className={classes.headerMenuIcon}>{item.icon || "🔗"}</span>
                        {item.label}
                        {item.type === "external" && (
                          <LaunchIcon className={classes.headerMenuLaunchIcon} />
                        )}
                      </MenuItem>
                    ))}
                  </Menu>
                </React.Fragment>
              ))}
              {headerMenuItems.map(item => (
                <Button
                  key={`${item.type}-${item.id || item.builtinKey}`}
                  size="small"
                  color="primary"
                  className={classes.headerMenuButton}
                  {...getHeaderMenuTarget(item)}
                >
                  <span className={classes.headerMenuIcon}>{item.icon || "🔗"}</span>
                  {item.label}
                  {item.type === "external" && (
                    <LaunchIcon className={classes.headerMenuLaunchIcon} />
                  )}
                </Button>
              ))}
            </div>
          )}

          {!hideResellerHeaderControls && canShowSidepanelAppsMenu && (
            <>
              <Button
                size="small"
                color="primary"
                className={classes.headerMenuButton}
                onClick={event => setSidepanelAppsAnchor(event.currentTarget)}
              >
                <span className={classes.headerMenuIcon}>{"\uD83D\uDCF1"}</span>
                Apps
              </Button>
              <Menu
                anchorEl={sidepanelAppsAnchor}
                open={Boolean(sidepanelAppsAnchor)}
                onClose={() => setSidepanelAppsAnchor(null)}
                getContentAnchorEl={null}
                anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
                transformOrigin={{ vertical: "top", horizontal: "left" }}
              >
                {loadingSidepanelApps && (
                  <MenuItem disabled>Cargando apps...</MenuItem>
                )}
                {!loadingSidepanelApps && !hasSidepanelApps && (
                  <MenuItem disabled>No hay apps configuradas</MenuItem>
                )}
                {!loadingSidepanelApps && sidepanelGlobalApps.length > 0 && (
                  <MenuItem disabled>Globales</MenuItem>
                )}
                {!loadingSidepanelApps &&
                  sidepanelGlobalApps.map(app => (
                    renderSidepanelAppMenuItem(app, "global")
                  ))}
                {!loadingSidepanelApps && sidepanelCompanyApps.length > 0 && (
                  <MenuItem disabled>De esta cuenta</MenuItem>
                )}
                {!loadingSidepanelApps &&
                  sidepanelCompanyApps.map(app => (
                    renderSidepanelAppMenuItem(app, "company")
                  ))}
              </Menu>
            </>
          )}

          {expirationBadge && (
            <Tooltip title="Administrar suscripcion">
              <Chip
                size="small"
                className={classes.expirationChip}
                color={expirationBadge.color}
                label={expirationBadge.label}
                clickable={hasExpirationBadgeLinks}
                onClick={
                  hasExpirationBadgeLinks ? handleOpenExpirationLinks : undefined
                }
              />
            </Tooltip>
          )}
          <Menu
            id="expiration-badge-links-menu"
            anchorEl={expirationLinksAnchor}
            getContentAnchorEl={null}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
            open={Boolean(expirationLinksAnchor)}
            onClose={handleCloseExpirationLinks}
          >
            {expirationBadgeLinks.map(link => (
              <MenuItem
                key={`${link.label}-${link.url}`}
                component="a"
                href={link.url}
                target="_blank"
                rel="noopener noreferrer"
                onClick={handleCloseExpirationLinks}
              >
                {link.label}
              </MenuItem>
            ))}
          </Menu>

          {/* Theme toggle disabled. */}

          {!hideResellerHeaderControls && !forceNormalMode && canManageAssignmentMode && (
            <div className={classes.assignmentModeToggle}>
              <button
                type="button"
                className={clsx(classes.assignmentModeOption, {
                  [classes.assignmentModeOptionActive]:
                    assignmentMode === "multiagent",
                })}
                disabled={savingAssignmentMode}
                onClick={() => handleRequestAssignmentMode("multiagent")}
              >
                Multiagente
              </button>
              <button
                type="button"
                className={clsx(classes.assignmentModeOption, {
                  [classes.assignmentModeOptionActive]:
                    assignmentMode === "normal",
                })}
                disabled={savingAssignmentMode}
                onClick={() => handleRequestAssignmentMode("normal")}
              >
                Normal
              </button>
            </div>
          )}

          {showHeaderWebChat && (
            <Badge
              className={classes.supportChatBadge}
              badgeContent={supportChatUnread}
              color="secondary"
              invisible={supportChatUnread <= 0}
            >
              <Button
                color="primary"
                variant="outlined"
                size="small"
                startIcon={<ChatIcon />}
                className={classes.supportChatButton}
                onClick={handleOpenSupportChat}
              >
                <span className="supportChatButtonText">Chat Soporte</span>
              </Button>
            </Badge>
          )}

          {!hideResellerHeaderControls && user.id && hasMenuPermission(user, "tickets") && (
            <NotificationsPopOver className={classes.iconButton} />
          )}

          {!hideResellerHeaderControls && (
            <div>
              <IconButton
                aria-label="Opciones de privacidad"
                aria-controls="privacy-censor-menu"
                aria-haspopup="true"
                onClick={event => setPrivacyMenuAnchor(event.currentTarget)}
                className={clsx(classes.iconButton, {
                  [classes.privacyButtonActive]: hasActivePrivacyCensor(
                    privacyCensorSettings
                  ),
                })}
              >
                <VisibilityIcon />
              </IconButton>
              <Menu
                id="privacy-censor-menu"
                anchorEl={privacyMenuAnchor}
                getContentAnchorEl={null}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "right",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "right",
                }}
                open={Boolean(privacyMenuAnchor)}
                onClose={() => setPrivacyMenuAnchor(null)}
              >
                <MenuItem onClick={() => handleTogglePrivacyCensor("contactPhotos")}>
                  <Checkbox checked={privacyCensorSettings.contactPhotos} />
                  <ListItemText primary="Ocultar fotos de contactos" />
                </MenuItem>
                <MenuItem onClick={() => handleTogglePrivacyCensor("contactNames")}>
                  <Checkbox checked={privacyCensorSettings.contactNames} />
                  <ListItemText primary="Ocultar nombres de contactos" />
                </MenuItem>
                <MenuItem onClick={() => handleTogglePrivacyCensor("conversations")}>
                  <Checkbox checked={privacyCensorSettings.conversations} />
                  <ListItemText primary="Ocultar conversacion" />
                </MenuItem>
                <MenuItem onClick={() => handleTogglePrivacyCensor("ticketMetaTags")}>
                  <Checkbox checked={privacyCensorSettings.ticketMetaTags} />
                  <ListItemText primary="Ocultar area, agente y conexion" />
                </MenuItem>
              </Menu>
            </div>
          )}

          <div>
            <IconButton
              aria-label="account of current user"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleMenu}
              className={classes.iconButton}
            >
              <AccountCircle />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorEl}
              getContentAnchorEl={null}
              anchorOrigin={{
                vertical: "bottom",
                horizontal: "right",
              }}
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={menuOpen}
              onClose={handleCloseMenu}
            >
              <MenuItem onClick={handleOpenUserModal}>
                {i18n.t("mainDrawer.appBar.user.profile")}
              </MenuItem>
              <MenuItem onClick={handleClickLogout}>
                {i18n.t("mainDrawer.appBar.user.logout")}
              </MenuItem>
            </Menu>
          </div>
        </Toolbar>
      </AppBar>
      <Dialog
        open={sidepanelExtensionModalOpen}
        onClose={() => setSidepanelExtensionModalOpen(false)}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Instalar extension de sidepanel</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            No se detecto la extension de sidepanel en este navegador. Instalala
            para abrir tus apps dentro del panel lateral.
          </DialogContentText>
          {sidepanelExtensionInstallUrl ? (
            <Typography variant="body2">
              Link:{" "}
              <a
                href={sidepanelExtensionInstallUrl}
                target="_blank"
                rel="noopener noreferrer"
              >
                {sidepanelExtensionInstallUrl}
              </a>
            </Typography>
          ) : (
            <Typography variant="body2" color="textSecondary">
              El superadministrador aun no configuro el link de instalacion.
            </Typography>
          )}
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setSidepanelExtensionModalOpen(false)}
            color="secondary"
          >
            Cerrar
          </Button>
          {sidepanelExtensionInstallUrl && (
            <Button
              color="primary"
              variant="contained"
              component="a"
              href={sidepanelExtensionInstallUrl}
              target="_blank"
              rel="noopener noreferrer"
            >
              Instalar extension
            </Button>
          )}
        </DialogActions>
      </Dialog>
      <Dialog
        open={Boolean(pendingAssignmentMode)}
        onClose={handleCloseAssignmentModeDialog}
        fullWidth
        maxWidth="xs"
      >
        <DialogTitle>Cambiar modo de bandeja</DialogTitle>
        <DialogContent dividers>
          <DialogContentText>
            {pendingAssignmentMode === "normal"
              ? "Al activar Normal, los chats pendientes se abriran y se asignaran al administrador principal. Los nuevos chats entraran directo a esa bandeja."
              : "Al volver a Multiagente, los nuevos chats entraran como pendientes para que los agentes los acepten normalmente. Los chats ya asignados no se moveran."}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={handleCloseAssignmentModeDialog}
            color="secondary"
            disabled={savingAssignmentMode}
          >
            Cancelar
          </Button>
          <Button
            onClick={handleConfirmAssignmentMode}
            color="primary"
            variant="contained"
            disabled={savingAssignmentMode}
          >
            Confirmar
          </Button>
        </DialogActions>
      </Dialog>
      <main className={classes.content}>
        <div className={classes.appBarSpacer} />
        {impersonation && (
          <div className={classes.impersonationBar}>
            <Typography className={classes.impersonationText} variant="body2">
              Ingresando como {user.name} ({user.email})
            </Typography>
            <Button
              size="small"
              variant="contained"
              onClick={handleStopImpersonation}
            >
              Volver a superadministrador
            </Button>
          </div>
        )}
        {children ? children : null}
      </main>
    </div>
  );
};

export default LoggedInLayout;

